import { useCallback, useMemo } from 'react';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { LogService } from '../../services/log-service';

import { getWalletRequirements } from '../utilities/api';

const initialRequirements = {};
const LABEL_PROVIDER_REQUIREMENTS = 'labelProviderRequirements';
const LABEL_PROVIDER_UPDATE_REQUIREMENTS = 'labelProviderUpdateRequirements';
const INSURANCE_PROVIDER_REQUIREMENTS = 'insuranceProviderRequirements';

const nonDefaultRequirementType = 'RequestActivation';

const addRequirement = (initialRequirement, requirement) => {
  const uniqueSatisfyingFormTypes = new Set([
    ...(initialRequirement?.satisfyingFormTypes || []),
    ...(requirement?.satisfyingFormTypes || []),
  ]);

  return {
    satisfyingFormTypes: Array.from(uniqueSatisfyingFormTypes),
  };
};

const buildRequirements = (registrationRequirements = {}) => {
  const allRequirements = Object.keys(registrationRequirements).flatMap((key) => {
    if (key === LABEL_PROVIDER_REQUIREMENTS) {
      return registrationRequirements[key]?.flatMap((entry) => entry?.requirements || []) || [];
    }
    if (key === LABEL_PROVIDER_UPDATE_REQUIREMENTS) {
      return registrationRequirements[key]?.flatMap((entry) => entry?.terms || []) || [];
    }
    if (key === INSURANCE_PROVIDER_REQUIREMENTS) {
      return registrationRequirements[key]?.flatMap((entry) => entry?.terms || []) || [];
    }
    return registrationRequirements[key]?.requirements || [];
  });

  return allRequirements.reduce((aggRequirements, requirement) => {
    const { requirementType } = requirement;
    return {
      ...aggRequirements,
      [requirementType]: addRequirement(aggRequirements[requirementType], requirement),
    };
  }, {});
};

export const buildTerms = (registrationRequirements = {}) => {
  const allTerms = Object.keys(registrationRequirements).flatMap((key) => {
    if (key === LABEL_PROVIDER_REQUIREMENTS) {
      return registrationRequirements[key]?.flatMap((providerSpecificRequirements) => {
        const requirements = providerSpecificRequirements?.requirements ?? [];
        const terms = providerSpecificRequirements?.terms ?? [];
        const isDefaultProvider = requirements.every((requirement) => requirement.requirementType !== nonDefaultRequirementType);
        return isDefaultProvider ? terms : [];
      });
    }
    if (key === LABEL_PROVIDER_UPDATE_REQUIREMENTS) {
      return registrationRequirements[key]?.flatMap((entry) => entry?.terms || []) || [];
    }
    if (key === INSURANCE_PROVIDER_REQUIREMENTS) {
      return registrationRequirements[key]?.flatMap((entry) => entry?.terms || []) || [];
    }
    return registrationRequirements[key]?.terms || [];
  });

  const uniqueTermsHashMap = allTerms.reduce(
    (aggTerms, term) => ({
      ...aggTerms,
      [term.termType]: term,
    }),
    {},
  );

  return Object.values(uniqueTermsHashMap);
};

export const buildNonDefaultTerms = (registrationRequirements = {}) => {
  const providerRequirements = registrationRequirements[LABEL_PROVIDER_REQUIREMENTS] ?? [];
  const nonDefaultLabelProviders = providerRequirements.filter((providerSpecificRequirements) => {
    const requirements = providerSpecificRequirements?.requirements ?? [];
    return requirements.some((requirement) => requirement.requirementType === nonDefaultRequirementType);
  });
  return Object.fromEntries(nonDefaultLabelProviders.map(({ labelProviderId, terms }) => [labelProviderId, terms]));
};

export const useWalletRequirements = () => {
  const [requirements, setRequirements] = useAsyncSafeState(initialRequirements);
  const [loading, setLoading] = useAsyncSafeState(false);

  const getRequirements = useCallback(
    async (walletId, currencyCode, labelProviderIds, resourceUpdateIds, insuranceProviderIds) => {
      setLoading(true);
      try {
        const results = await getWalletRequirements(walletId, currencyCode, labelProviderIds, resourceUpdateIds, insuranceProviderIds);
        setRequirements(results);
      } catch (e) {
        setRequirements(initialRequirements);
        LogService.logError(new Error('Error getting wallet requirements', { cause: e }));
      }
      setLoading(false);
    },
    [setRequirements, setLoading],
  );

  const consolidatedRequirements = useMemo(() => {
    return buildRequirements(requirements);
  }, [requirements]);

  const consolidatedTerms = useMemo(() => {
    return buildTerms(requirements);
  }, [requirements]);

  const consolidatedNonDefaultTerms = useMemo(() => {
    return buildNonDefaultTerms(requirements);
  }, [requirements]);

  return useMemo(
    () => ({
      requirements,
      loading,
      consolidatedRequirements,
      consolidatedTerms,
      consolidatedNonDefaultTerms,
      getRequirements,
    }),
    [consolidatedNonDefaultTerms, consolidatedRequirements, consolidatedTerms, getRequirements, loading, requirements],
  );
};
